import React, { useState, useEffect } from 'react';
import { videosAPI, filesAPI, playlistsAPI } from '../services/api';
import TagSelect from './TagSelect';
import PlaylistSelect from './PlaylistSelect';
import UpgradeBanner from './UpgradeBanner';
import YouTubePlaylistImport from './YouTubePlaylistImport';
import AddPlaylistForm from './AddPlaylistForm';

const AddInspirationModal = ({ isOpen, onClose, onSuccess, onPlaylistCreated, defaultTab, defaultVideoMode, videoLimitReached = false, fileLimitReached = false, playlistLimitReached = false }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || 'video'); // 'video', 'file', or 'playlist'
  const [fileInputMode, setFileInputMode] = useState('upload'); // 'upload' or 'link'
  const [videoInputMode, setVideoInputMode] = useState('youtube'); // 'youtube' or 'single'

  // When modal opens with a defaultTab (e.g. from Files tab), switch to that section
  useEffect(() => {
    if (isOpen && defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  useEffect(() => {
    if (isOpen) {
      setVideoInputMode(defaultVideoMode || 'youtube');
    }
  }, [isOpen, defaultVideoMode]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');

  // Video form data
  const [videoData, setVideoData] = useState({
    embed_id: ''
  });

  // File form data
  const [fileData, setFileData] = useState({
    title: '',
    file: null,
    preview: null,
    imageUrl: ''
  });

  const tagPayload = () => {
    const tagIds = selectedTags.filter(t => t.id).map(t => t.id);
    const tagNames = selectedTags.filter(t => !t.id).map(t => (t.icon ? { tag: t.tag, icon: t.icon } : t.tag));
    return { tag_ids: tagIds, tag_names: tagNames };
  };

  const getCreatedFileId = (response) => {
    if (!response) return null;
    if (response.id != null) return Number(response.id);
    if (response.data?.id != null) return Number(response.data.id);
    return null;
  };

  const getCreatedVideoId = (response) => {
    if (!response) return null;
    if (response.id != null) return Number(response.id);
    if (response.data?.id != null) return Number(response.data.id);
    return null;
  };

  const handlePlaylistCreated = (playlist) => {
    setPlaylists((prev) => [...prev, playlist]);
    onPlaylistCreated?.(playlist);
  };

  const handlePlaylistAdded = () => {
    onSuccess();
    onClose();
  };

  useEffect(() => {
    let cancelled = false;
    if (!isOpen) return;

    playlistsAPI.getAll({ preview: true })
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response) ? response : (response?.data ?? []);
        setPlaylists(list);
      })
      .catch(() => {
        if (!cancelled) setPlaylists([]);
      });

    return () => { cancelled = true; };
  }, [isOpen]);

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    if (!videoData.embed_id) {
      setError('Please enter a YouTube URL or video ID');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await videosAPI.create({ embed_id: videoData.embed_id, ...tagPayload() });
      const createdVideoId = getCreatedVideoId(response);
      if (selectedPlaylistId && createdVideoId) {
        await videosAPI.addToPlaylist(createdVideoId, Number(selectedPlaylistId));
      }
      onSuccess();
      onClose();
      setVideoData({ embed_id: '' });
      setSelectedTags([]);
      setSelectedPlaylistId('');
    } catch (err) {
      console.error('Error creating video:', err);
      setError(err.data?.message || 'Failed to add video');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileData({
        ...fileData,
        file: file,
        preview: URL.createObjectURL(file)
      });
    }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (fileInputMode === 'link') {
      if (!fileData.imageUrl?.trim() || !fileData.title?.trim()) {
        setError('Please provide a title and image URL');
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await filesAPI.create({
          title: fileData.title.trim(),
          url: fileData.imageUrl.trim(),
          ...tagPayload()
        });
        const createdFileId = getCreatedFileId(response);
        if (selectedPlaylistId && createdFileId) {
          await filesAPI.addToPlaylist(createdFileId, Number(selectedPlaylistId));
        }
        onSuccess();
        onClose();
        setFileData({ title: '', file: null, preview: null, imageUrl: '' });
        setSelectedTags([]);
        setSelectedPlaylistId('');
      } catch (err) {
        console.error('Error adding image from link:', err);
        setError(err.data?.message || 'Failed to add image from link');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!fileData.file || !fileData.title) {
      setError('Please provide a title and select a file');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        const filePayload = {
          name: fileData.file.name,
          title: fileData.title,
          data: base64Data,
          ...tagPayload()
        };

        try {
          const response = await filesAPI.create(filePayload);
          const createdFileId = getCreatedFileId(response);
          if (selectedPlaylistId && createdFileId) {
            await filesAPI.addToPlaylist(createdFileId, Number(selectedPlaylistId));
          }
          onSuccess();
          onClose();
          setFileData({ title: '', file: null, preview: null, imageUrl: '' });
          setSelectedTags([]);
          setSelectedPlaylistId('');
        } catch (err) {
          console.error('Error creating file:', err);
          setError(err.data?.message || 'Failed to upload file');
        } finally {
          setLoading(false);
        }
      };
      reader.onerror = () => {
        console.error('Error reading file:', reader.error);
        setError('Failed to read the selected file');
        setLoading(false);
      };
      reader.readAsDataURL(fileData.file);
    } catch (err) {
      console.error('Error processing file:', err);
      setError('Failed to process file');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPlaylistId('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] m-0 p-0" style={{ top: 0, left: 0, right: 0, bottom: 0, margin: 0, padding: 0 }}>
      <div className="bg-slate-50 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-slate-800 font-venti">Add Inspiration</h3>
            <button
              onClick={handleClose}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('video')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'video'
                  ? 'text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              style={activeTab === 'video' ? { backgroundColor: '#ea3663' } : {}}
            >
              YouTube Video
            </button>
            <button
              onClick={() => setActiveTab('file')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'file'
                  ? 'text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              style={activeTab === 'file' ? { backgroundColor: '#ea3663' } : {}}
            >
              Image or PDF
            </button>
            <button
              onClick={() => setActiveTab('playlist')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'playlist'
                  ? 'text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              style={activeTab === 'playlist' ? { backgroundColor: '#ea3663' } : {}}
            >
              Playlist
            </button>
          </div>
        </div>

        {/* Same height for every tab and option so switching doesn't resize the modal */}
        <div className="p-6 h-[35rem] flex flex-col overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {activeTab === 'video' && videoLimitReached && (
            <UpgradeBanner itemType="videos" />
          )}
          {activeTab === 'file' && fileLimitReached && (
            <UpgradeBanner itemType="other files" />
          )}
          {activeTab === 'playlist' && playlistLimitReached && (
            <UpgradeBanner itemType="playlists" />
          )}

          {activeTab === 'playlist' && !playlistLimitReached && (
            <AddPlaylistForm onSuccess={handlePlaylistAdded} onCancel={handleClose} />
          )}

          {activeTab === 'video' && !videoLimitReached && (
            <div className="flex flex-shrink-0 space-x-2 mb-4">
              {[
                { id: 'youtube', label: 'Import videos by playlist' },
                { id: 'single', label: 'Import single video' },
              ].map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => { setVideoInputMode(mode.id); setError(null); }}
                  disabled={loading}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    videoInputMode === mode.id ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  style={videoInputMode === mode.id ? { backgroundColor: '#ea3663' } : {}}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'video' && !videoLimitReached && videoInputMode === 'youtube' && (
            <div className="flex min-h-0 flex-1 flex-col">
              <YouTubePlaylistImport onImported={handleClose} onCancel={handleClose} />
            </div>
          )}

          {/* Video Tab */}
          {activeTab === 'video' && !videoLimitReached && videoInputMode === 'single' && (
            <form onSubmit={handleVideoSubmit} className="flex flex-1 flex-col space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  YouTube URL or Video ID *
                </label>
                <input
                  type="text"
                  value={videoData.embed_id}
                  onChange={(e) => setVideoData({ ...videoData, embed_id: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg text-slate-800"
                  placeholder="https://www.youtube.com/watch?v=... or video ID"
                  required
                />
                <p className="mt-2 text-xs text-slate-500">
                  Enter a YouTube URL or video ID. The video title and thumbnail will be automatically fetched.
                </p>
              </div>
              <TagSelect value={selectedTags} onChange={setSelectedTags} disabled={loading} />
              <PlaylistSelect
                playlists={playlists}
                onPlaylistCreated={handlePlaylistCreated}
                value={selectedPlaylistId}
                onChange={setSelectedPlaylistId}
                disabled={loading}
              />
              <div className="flex justify-end space-x-3 pt-4 !mt-auto">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#ea3663' }}
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Video'}
                </button>
              </div>
            </form>
          )}

          {/* File Tab */}
          {activeTab === 'file' && !fileLimitReached && (
            <form onSubmit={handleFileSubmit} className="flex flex-1 flex-col space-y-4">
              <div className="flex space-x-2 mb-4">
                <button
                  type="button"
                  onClick={() => { setFileInputMode('upload'); setFileData(prev => ({ ...prev, imageUrl: '' })); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    fileInputMode === 'upload' ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  style={fileInputMode === 'upload' ? { backgroundColor: '#ea3663' } : {}}
                >
                  Upload file
                </button>
                <button
                  type="button"
                  onClick={() => { setFileInputMode('link'); setFileData(prev => ({ ...prev, file: null, preview: null })); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    fileInputMode === 'link' ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  style={fileInputMode === 'link' ? { backgroundColor: '#ea3663' } : {}}
                >
                  From link
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={fileData.title}
                  onChange={(e) => setFileData({ ...fileData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg text-slate-800"
                  placeholder="Enter a title for this file"
                  required
                />
              </div>
              <TagSelect value={selectedTags} onChange={setSelectedTags} disabled={loading} />
              <PlaylistSelect
                playlists={playlists}
                onPlaylistCreated={handlePlaylistCreated}
                value={selectedPlaylistId}
                onChange={setSelectedPlaylistId}
                disabled={loading}
              />
              {fileInputMode === 'link' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Image URL *
                  </label>
                  <input
                    type="url"
                    value={fileData.imageUrl}
                    onChange={(e) => setFileData({ ...fileData, imageUrl: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg text-slate-800"
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Enter a direct link to an image (JPG, PNG, GIF). The image will be downloaded and saved.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    File (Image or PDF) *
                  </label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg text-slate-800"
                    required={fileInputMode === 'upload'}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Supported formats: JPG, PNG, GIF, PDF
                  </p>
                  {fileData.preview && (
                    <div className="mt-4">
                      <p className="text-sm text-slate-600 mb-2">Preview:</p>
                      {fileData.file.type.startsWith('image/') ? (
                        <img
                          src={fileData.preview}
                          alt="Preview"
                          className="max-w-full h-auto rounded-lg border border-slate-200"
                          style={{ maxHeight: '300px' }}
                        />
                      ) : (
                        <div className="p-4 bg-slate-100 rounded-lg border border-slate-200">
                          <p className="text-sm text-slate-600">PDF: {fileData.file.name}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-4 !mt-auto">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#ea3663' }}
                  disabled={loading}
                >
                  {loading
                    ? (fileInputMode === 'link' ? 'Adding...' : 'Uploading...')
                    : (fileInputMode === 'link' ? 'Add from link' : 'Upload File')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddInspirationModal;

